// src/components/features/Structure/pages/StructureFormPage.jsx
// Main page for Structure module — mirrors PlanningFormPage structure
// URL: /structure/:projectId?token=xxx&updatedby=admin

import { useState, useEffect, useCallback, useRef } from "react";
import { useParams, useSearchParams, useNavigate } from "react-router-dom";
import {
  Box,
  Flex,
  useColorModeValue,
  useBreakpointValue,
  useToast,
  Spinner,
  Center,
  Text,
  Badge,
} from "@chakra-ui/react";

import {
  isAnyAuthenticated,
  saveRedirectUrl,
  saveAdminToken,
  getAdminToken,
  getUpdatedBy,
  getCustomerToken,
} from "../../../../utils/storage";
import {
  fetchFormData,
  postStructureFormData,
  putStructureFormData,
  moveCardToInProgress,
} from "../../../../utils/api";

import StructureStep1Location from "../components/StructureStep1Location";
import StructureStep2SoilType from "../components/StructureStep2SoilType";
import StructureStep3FootingType from "../components/StructureStep3FootingType";
import StructureStep4FloorPlan from "../components/StructureStep4FloorPlan";
import StructureStep5Stairs from "../components/StructureStep5Stairs";

// Default form data for Structure module
const DEFAULT_STRUCTURE_FORM_DATA = {
  // Step 1 — Location & Seismic Zone
  structureCity: "",
  seismicZone: "",
  seismicRisk: "",

  // Step 2 — Soil Type
  soilType: "",   // black | red | yellow
  soilCondition: "",   // loose | medium | hard

  // Step 3 — Footing Type
  footingType: "",   // pile | isolated

  // Step 4 — Floor Plan Heights (shared with Planning module fields)
  plinthLevelFeet: "", plinthLevelInches: "0",
  lintelLevelFeet: "", lintelLevelInches: "0",
  sillLevelFeet: "", sillLevelInches: "0",
  slabHeightFeet: "", slabHeightInches: "0",

  // Step 5 — Stair Structure Type
  structureStairType: "",  // folded | waist
};

const TOTAL_STEPS = 5;

export default function StructureFormPage() {
  const { projectId: projectIdFromPath } = useParams();
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const toast = useToast();

  // "/structure/:projectId" (path param) aur "/structure?projectId=xxx"
  // (query param) — dono URL shapes se projectId mil sake, isliye fallback.
  const projectId =
    projectIdFromPath ||
    searchParams.get("projectId") ||
    searchParams.get("pid") ||
    searchParams.get("id") ||
    "";

  const urlToken = searchParams.get("token") || "";
  const urlUpdatedBy = searchParams.get("updatedby") || "";
  const formVersion = searchParams.get("version") || "";
  // VIEW MODE: ?viewMode=true par form sirf padhne ke liye khulega —
  // next/previous chalega par koi PUT/POST API call nahi hogi, aur
  // thank-you page pe bhi kabhi redirect nahi hoga.
  const viewMode = searchParams.get("viewMode") === "true";

  if (urlToken) {
    saveAdminToken(urlToken, urlUpdatedBy);
  }

  const isAdmin = !!(urlToken || (urlUpdatedBy === "admin" && getAdminToken()));
  const token = urlToken || (isAdmin ? getAdminToken() : getCustomerToken()) || "";
  const updatedBy = isAdmin ? "admin" : (urlUpdatedBy || getUpdatedBy() || "user");

  const [isLoading, setIsLoading] = useState(true);
  const [isSaving, setIsSaving] = useState(false);
  const [apiError, setApiError] = useState(null);
  const [step, setStep] = useState(0);
  const [formData, setFormData] = useState(DEFAULT_STRUCTURE_FORM_DATA);
  const [version, setVersion] = useState(null);

  const moveCardCalled = useRef(false);

  const containerWidth = useBreakpointValue({ base: "100%", md: "400px" });
  const pageBg = useColorModeValue("gray.100", "gray.900");
  const cardBg = useColorModeValue("gray.50", "gray.800");

  const initialize = useCallback(async () => {
    if (!token && !isAnyAuthenticated()) {
      saveRedirectUrl(window.location.pathname + window.location.search);
      toast({
        title: "Authentication Required",
        description: "Please login to continue",
        status: "info",
        duration: 3000,
        isClosable: true,
      });
      setTimeout(() => navigate("/login"), 1500);
      return;
    }

    if (!projectId) {
      setIsLoading(false);
      return;
    }

    try {
      setIsLoading(true);
      setApiError(null);

      const apiFormData = await fetchFormData(projectId, token, updatedBy, formVersion);

      // FIX: pehle "pending" par thank-you page pe redirect ho raha tha, jo
      // ulta tha — pending ka matlab form abhi incomplete hai, usko form hi
      // dikhna chahiye. Thank-you sirf tab dikhna chahiye jab status pehle se
      // "submitted" ho chuka ho (PlanningFormPage jaisa hi fix).
      // NEW: view mode me kabhi bhi thank-you pe redirect nahi karna — view
      // mode sirf data dekhne ke liye hai, status kuch bhi ho poora form
      // dikhna chahiye.
      if (apiFormData._meta?.status === "submitted" && !viewMode) {
        navigate("/thank-you");
        return;
      }

      if (apiFormData._meta?.version) {
        setVersion(apiFormData._meta.version);
      }

      const { _meta, ...apiFormDataClean } = apiFormData;

      setFormData((prev) => ({
        ...DEFAULT_STRUCTURE_FORM_DATA,
        ...prev,
        ...apiFormDataClean,
      }));

      toast({
        title: "Project loaded ✓",
        description: `${projectId} — ${updatedBy === "admin" ? "Admin" : "User"} mode`,
        status: "success",
        duration: 3000,
        isClosable: true,
        position: "top",
      });

      // View mode me server ka state change nahi karna, isliye skip.
      if (!moveCardCalled.current && !viewMode) {
        moveCardCalled.current = true;
        await moveCardToInProgress(projectId, token, updatedBy, apiFormData._meta?.version || formVersion);
      }
    } catch (err) {
      console.error("Structure API fetch failed:", err);
      setApiError(err.message);
      // View mode me error toast nahi dikhana — sirf console me log rahega
      if (!viewMode) {
        toast({
          title: "Could not load project",
          description: `${err.message} — using blank form`,
          status: "warning",
          duration: 5000,
          isClosable: true,
          position: "top",
        });
      }
    } finally {
      setIsLoading(false);
    }
  }, [projectId, token, updatedBy, navigate, toast, viewMode, formVersion]);

  useEffect(() => {
    initialize();
  }, [initialize]);

  // PUT on every Next step
  const putToApi = async (data) => {
    // View mode: koi save API nahi chalegi — sirf navigation allowed
    if (!projectId || viewMode) return;
    setIsSaving(true);
    try {
      await putStructureFormData(projectId, data, updatedBy, token, version);
    } catch (err) {
      console.error("Structure step save (PUT) failed:", err);
      toast({
        title: "Step save failed",
        description: err.message,
        status: "error",
        duration: 3000,
        isClosable: true,
        position: "top-right",
      });
    } finally {
      setIsSaving(false);
    }
  };

  // POST on final submit
  const postToApi = async (data) => {
    // View mode: final submit bhi server par nahi jaayega
    if (!projectId || viewMode) return;
    setIsSaving(true);
    try {
      await postStructureFormData(projectId, data, updatedBy, token, version);
    } catch (err) {
      console.error("Structure final save (POST) failed:", err);
      toast({
        title: "Save failed",
        description: err.message,
        status: "error",
        duration: 3000,
        isClosable: true,
        position: "top-right",
      });
    } finally {
      setIsSaving(false);
    }
  };

  const nextStep = async (latestData) => {
    const dataForPut = latestData ?? formData;
    await putToApi(dataForPut);
    setStep((p) => Math.min(p + 1, TOTAL_STEPS - 1));
  };

  const prevStep = () => setStep((p) => Math.max(p - 1, 0));
  const isLastStep = step === TOTAL_STEPS - 1;

  const handleSubmit = async (latestData) => {
    // View mode: na POST, na thank-you redirect — form sirf dekhne ke liye hai
    if (viewMode) return;
    const dataForPost = latestData ?? formData;
    await postToApi(dataForPost);
    navigate("/thank-you");
  };

  const renderStep = () => {
    const props = {
      formData, setFormData,
      onBack: prevStep, onNext: nextStep, onSubmit: handleSubmit,
      isLastStep, projectId, updatedBy, token, viewMode,
    };
    switch (step) {
      case 0: return <StructureStep1Location   {...props} />;
      case 1: return <StructureStep2SoilType   {...props} />;
      case 2: return <StructureStep3FootingType {...props} />;
      case 3: return <StructureStep4FloorPlan  {...props} />;
      case 4: return <StructureStep5Stairs     {...props} />;
      default: return null;
    }
  };

  if (isLoading) {
    return (
      <Flex minH="100vh" align="center" justify="center" bg={pageBg}>
        <Center flexDirection="column" gap={4}>
          <Spinner size="xl" color="cyan.500" thickness="4px" />
          <Text color="gray.500" fontSize="sm">Loading {projectId}...</Text>
        </Center>
      </Flex>
    );
  }

  return (
    <Flex minH="100vh" align="center" justify="center" bg={pageBg}>
      <Box
        w={containerWidth}
        bg={cardBg}
        px={6} py={6}
        borderRadius="lg"
        boxShadow="lg"
        display="flex"
        flexDirection="column"
      >
        {/* Header: Step counter + badges */}
        <Flex justify="space-between" align="center" mb={2}>
          <Flex align="center" gap={2}>
            <Text fontSize="11px" color="gray.400" fontWeight="500">
              Step {step + 1} / {TOTAL_STEPS}
            </Text>
            {projectId && (
              <Badge fontSize="10px" colorScheme="cyan" variant="subtle">
                {projectId}
              </Badge>
            )}
            {updatedBy === "admin" && (
              <Badge fontSize="10px" colorScheme="purple" variant="subtle">
                Admin
              </Badge>
            )}
            <Badge fontSize="10px" colorScheme="teal" variant="subtle">
              Structure
            </Badge>
            {viewMode && (
              <Badge fontSize="10px" colorScheme="orange" variant="solid">
                View Mode
              </Badge>
            )}
          </Flex>
          <Flex align="center" gap={1}>
            {isSaving && (
              <Text fontSize="10px" color="cyan.500" fontWeight="600">
                Saving...
              </Text>
            )}
            {apiError && (
              <Badge fontSize="10px" colorScheme="orange" variant="subtle">
                ⚠ Offline
              </Badge>
            )}
          </Flex>
        </Flex>

        {/* Progress Bar */}
        <Box bg="gray.200" borderRadius="full" h="3px" mb={4}>
          <Box
            bg="cyan.500"
            borderRadius="full"
            h="3px"
            w={`${((step + 1) / TOTAL_STEPS) * 100}%`}
            transition="width 0.3s ease"
          />
        </Box>

        <Box flex="1">{renderStep()}</Box>
      </Box>
    </Flex>
  );
}